/**
 * UserAvatar — shared avatar component used throughout the ERP.
 *
 * Renders the user's profile photo when available; falls back to a
 * coloured initials circle that matches the existing sidebar avatar style.
 *
 * Props:
 *   user     object   — user object with `name`, `profile_photo_url`, `role`,
 *                       `role_codes` fields. Accepts null/undefined gracefully.
 *   size     string   — "xs" | "sm" | "md" | "lg" | "xl"  (default: "md")
 *   onClick  fn|null  — click handler (adds cursor: pointer when provided)
 *   style    object   — extra inline styles on the root element
 *   className string  — extra CSS classes on the root element
 */
import React, { useState } from "react";

const SIZES = {
  xs: { px: 20, fs: 8,  fw: 600 },
  sm: { px: 28, fs: 11, fw: 600 },
  md: { px: 32, fs: 13, fw: 600 },
  lg: { px: 56, fs: 20, fw: 700 },
  xl: { px: 80, fs: 28, fw: 700 },
};

function isSuperAdminUser(user) {
  const codes = user?.role_codes || [];
  return codes.includes("super_admin") || user?.role === "super_admin";
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function UserAvatar({ user, size = "md", onClick, style, className }) {
  const { px, fs, fw } = SIZES[size] || SIZES.md;
  const [imgError, setImgError] = useState(false);
  const isSA = isSuperAdminUser(user);

  const baseStyle = {
    width: px,
    height: px,
    borderRadius: "50%",
    flexShrink: 0,
    cursor: onClick ? "pointer" : "default",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    ...style,
  };

  // Photo avatar
  if (user?.profile_photo_url && !imgError) {
    return (
      <div style={baseStyle} className={className} onClick={onClick}>
        <img
          src={user.profile_photo_url}
          alt={user?.name || "Avatar"}
          onError={() => setImgError(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </div>
    );
  }

  // Initials avatar
  return (
    <div
      style={{
        ...baseStyle,
        background: isSA
          ? "linear-gradient(135deg, #fef3c7, #fbbf24)"
          : "linear-gradient(135deg, var(--flg-neutral-100), var(--flg-neutral-200))",
        color: isSA ? "#78350f" : "var(--flg-fg-secondary)",
        fontSize: fs,
        fontWeight: fw,
        fontFamily: "var(--flg-font-display)",
        letterSpacing: "-0.01em",
        userSelect: "none",
      }}
      className={className}
      onClick={onClick}
    >
      {getInitials(user?.name)}
    </div>
  );
}

export default UserAvatar;
